import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { getActiveSession } from "@auth/sessionStore";

/** Mirrors brian's `PhoneLineOut` (modules/phone/schemas.py). */
export interface PhoneLine {
  inboundDidE164: string;
  enabled: boolean;
}

/** The `{purpose}` path segment. Mirrors brian's `MessagePurpose` (modules/phone/messages.py). */
export type PhoneMessagePurpose = components["schemas"]["MessagePurpose"];

/** In the order a held caller hears them. */
export type PhoneHoldMessagePurpose =
  | "hold_1"
  | "hold_2"
  | "hold_3"
  | "hold_4"
  | "hold_5";

export const HOLD_MESSAGE_PURPOSES: readonly PhoneHoldMessagePurpose[] = [
  "hold_1",
  "hold_2",
  "hold_3",
  "hold_4",
  "hold_5",
];

const isHoldMessagePurpose = (
  purpose: PhoneMessagePurpose,
): purpose is PhoneHoldMessagePurpose =>
  (HOLD_MESSAGE_PURPOSES as readonly string[]).includes(purpose);

interface PhoneMessageAudio {
  filename: string;
  uploadedAt: string;
}

/** A short-lived presigned GET. Mirrors brian's `PhoneMessageAudioUrlOut`. */
interface PhoneMessageAudioUrl {
  downloadUrl: string;
  filename: string;
  contentType: string;
  uploadedAt: string;
}

/** `effectiveText` is the TTS callers hear; null when audio plays instead. Mirrors brian's `PhoneMessageOut`. */
export interface PhoneMessageConfig {
  /** "unset": a hold message with no default that hasn't been written. */
  mode: "default" | "text" | "audio" | "unset";
  customText: string | null;
  defaultText: string | null;
  effectiveText: string | null;
  audio: PhoneMessageAudio | null;
}

/** Audio mode is entered by uploading, never by this input. */
export type PhoneMessageInput =
  | { mode: "default" }
  | { mode: "text"; text: string };

/**
 * Only `hold_1` has a default; saving `{mode: "default"}` on the others unsets
 * them, which is "remove". Mirrors brian's `PhoneHoldMessageOut`.
 */
export interface PhoneHoldMessage extends PhoneMessageConfig {
  purpose: PhoneHoldMessagePurpose;
}

/** Mirrors brian's `PhoneHoldMusicOut` and the `hold_*` fields of `PhoneSetupOut`. */
export interface PhoneHoldSetup {
  messages: PhoneHoldMessage[];
  music: { custom: boolean; audio: PhoneMessageAudio | null };
  checkInSeconds: number;
  maxSeconds: number;
}

/** Mirrors brian's `OutboundEndpointOut` (modules/phone/schemas.py). */
export interface OutboundLine {
  id: string;
  label: string;
  /** `browser` is the signed-in user's own computer. */
  kind: "pstn" | "browser";
  addressE164: string;
  enabled: boolean;
  ringTimeoutSeconds: number;
}

/** `address` as typed; brian normalizes it. Mirrors brian's `OutboundEndpointIn`. */
export interface OutboundLineInput {
  label: string;
  address: string;
  enabled: boolean;
  ringTimeoutSeconds: number;
}

/** Until `verified`, recipients see Brian's own line. Mirrors brian's `CallerIdVerificationOut`. */
interface CallerId {
  numberE164: string | null;
  verified: boolean;
  verificationPending: boolean;
}

/** Mirrors brian's `InboundRingMode` (models/clinic_phone_config.py). */
export type InboundRingMode = components["schemas"]["InboundRingMode"];

export const INBOUND_RING_MODES: readonly InboundRingMode[] = [
  "clinic_phones",
  "clinic_phones_and_computers",
  "computers_only",
];

export const inboundRingsComputers = (mode: InboundRingMode): boolean =>
  mode !== "clinic_phones";

/** `line` is null until operations provisions the number. Mirrors brian's `PhoneSetupOut`. */
export interface PhoneSetup {
  line: PhoneLine | null;
  messages: {
    recordingDisclosure: PhoneMessageConfig;
    voicemailGreeting: PhoneMessageConfig;
    outboundRecordingDisclosure: PhoneMessageConfig;
  };
  handoffDestinations: string[];
  outboundEnabled: boolean;
  outboundConcurrencyLimit: number;
  /** Off: staff give the recording notice themselves. */
  outboundDisclosureSpoken: boolean;
  inboundRing: InboundRingMode;
  /** A Call rings the caller's own headset instead of asking which line. */
  outboundComputerDefault: boolean;
  callerId: CallerId;
  outboundLines: OutboundLine[];
  hold: PhoneHoldSetup;
}

const toMessageAudio = (
  a: components["schemas"]["PhoneMessageAudioOut"] | null,
): PhoneMessageAudio | null =>
  a ? { filename: a.filename, uploadedAt: a.uploaded_at } : null;

const toMessageConfig = (
  m: components["schemas"]["PhoneMessageOut"],
): PhoneMessageConfig => ({
  mode: m.mode,
  customText: m.custom_text,
  defaultText: m.default_text,
  effectiveText: m.effective_text,
  audio: toMessageAudio(m.audio),
});

type PhoneSetupOut = components["schemas"]["PhoneSetupOut"];

const toHoldSetup = (s: PhoneSetupOut): PhoneHoldSetup => {
  const messages: PhoneHoldMessage[] = [];
  for (const m of s.hold_messages) {
    if (!isHoldMessagePurpose(m.purpose)) continue;
    messages.push({ ...toMessageConfig(m), purpose: m.purpose });
  }
  return {
    messages,
    music: {
      custom: s.hold_music.mode === "audio",
      audio: toMessageAudio(s.hold_music.audio),
    },
    checkInSeconds: s.hold_check_in_seconds,
    maxSeconds: s.hold_max_seconds,
  };
};

const toPhoneSetup = (s: PhoneSetupOut): PhoneSetup => ({
  line: s.line
    ? { inboundDidE164: s.line.inbound_did_e164, enabled: s.line.enabled }
    : null,
  messages: {
    recordingDisclosure: toMessageConfig(s.messages.recording_disclosure),
    voicemailGreeting: toMessageConfig(s.messages.voicemail_greeting),
    outboundRecordingDisclosure: toMessageConfig(
      s.messages.outbound_recording_disclosure,
    ),
  },
  handoffDestinations: s.handoff_destinations,
  outboundEnabled: s.outbound_enabled,
  outboundConcurrencyLimit: s.outbound_concurrency_limit,
  outboundDisclosureSpoken: s.outbound_disclosure_spoken,
  inboundRing: s.inbound_ring,
  outboundComputerDefault: s.outbound_computer_default,
  callerId: {
    numberE164: s.outbound_caller_id_e164,
    verified: s.outbound_caller_id_verified,
    verificationPending: s.outbound_caller_id_verification_pending,
  },
  outboundLines: s.outbound_endpoints.map(toOutboundLine),
  hold: toHoldSetup(s),
});

const toOutboundLine = (
  e: components["schemas"]["OutboundEndpointOut"],
): OutboundLine => ({
  id: e.uuid,
  label: e.label,
  kind: e.kind,
  addressE164: e.address_e164,
  enabled: e.enabled,
  ringTimeoutSeconds: e.ring_timeout_seconds,
});

const toCallerId = (
  c: components["schemas"]["CallerIdVerificationOut"],
): CallerId => ({
  numberE164: c.outbound_caller_id_e164,
  verified: c.outbound_caller_id_verified,
  verificationPending: c.outbound_caller_id_verification_pending,
});

/** One entry of FastAPI's 422 `detail`. */
interface PhoneSetupFieldError {
  loc: (string | number)[];
  msg: string;
}

/** Extends {@link ClinicAuthError} so `describeMutationError` still handles it. */
export class PhoneSetupValidationError extends ClinicAuthError {
  readonly fieldErrors: PhoneSetupFieldError[];

  constructor(fieldErrors: PhoneSetupFieldError[]) {
    super("unknown", "HTTP 422", 422);
    this.fieldErrors = fieldErrors;
  }
}

const isFieldError = (entry: unknown): entry is PhoneSetupFieldError =>
  typeof entry === "object" &&
  entry !== null &&
  Array.isArray((entry as PhoneSetupFieldError).loc) &&
  typeof (entry as PhoneSetupFieldError).msg === "string";

/** Like {@link request}, but keeps a 422's field-level `detail`. */
async function requestKeeping422<T>(
  call: () => Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  if (!getActiveSession())
    throw new ClinicAuthError("invalid_credentials", "No active session");

  let result;
  try {
    result = await call();
  } catch {
    throw new ClinicAuthError("network_error");
  }
  if (result.data !== undefined) return result.data;

  const status = result.response.status;
  if (status === 401) throw new ClinicAuthError("invalid_credentials");
  if (status === 422) {
    const detail = (result.error as { detail?: unknown } | undefined)?.detail;
    if (Array.isArray(detail) && detail.every(isFieldError))
      throw new PhoneSetupValidationError(detail);
  }
  throw new ClinicAuthError("unknown", `HTTP ${status}`, status);
}

export const phoneSetupClient = {
  get: (): Promise<PhoneSetup> =>
    request(() => apiClient.GET("/phone-setup", {})).then(toPhoneSetup),

  /** Enabled lines plus ring settings, readable by any signed-in user; `get` is leadership-only. */
  getOutboundLines: (): Promise<{
    lines: OutboundLine[];
    inboundRing: InboundRingMode;
    outboundComputerDefault: boolean;
    outboundDisclosureSpoken: boolean;
  }> =>
    request(() => apiClient.GET("/phone-setup/outbound-endpoints", {})).then(
      (saved) => ({
        lines: saved.endpoints.map(toOutboundLine),
        inboundRing: saved.inbound_ring,
        outboundComputerDefault: saved.outbound_computer_default,
        outboundDisclosureSpoken: saved.outbound_disclosure_spoken,
      }),
    ),

  setMessage: (
    purpose: PhoneMessagePurpose,
    input: PhoneMessageInput,
  ): Promise<PhoneMessageConfig> =>
    requestKeeping422(() =>
      apiClient.PUT("/phone-setup/messages/{purpose}", {
        params: { path: { purpose } },
        body: input,
      }),
    ).then(toMessageConfig),

  uploadMessageAudio: (
    purpose: PhoneMessagePurpose,
    file: File,
  ): Promise<PhoneMessageConfig> =>
    requestKeeping422(() =>
      apiClient.PUT("/phone-setup/messages/{purpose}/audio", {
        params: { path: { purpose } },
        // openapi-typescript types binary parts as `string`; the serializer sends the File.
        body: { file: file as unknown as string },
        bodySerializer: () => {
          const form = new FormData();
          form.append("file", file);
          return form;
        },
      }),
    ).then(toMessageConfig),

  /** Short-lived: fetch one per play. 404 when no audio. */
  getMessageAudioUrl: (
    purpose: PhoneMessagePurpose,
  ): Promise<PhoneMessageAudioUrl> =>
    requestKeeping422(() =>
      apiClient.GET("/phone-setup/messages/{purpose}/audio", {
        params: { path: { purpose } },
      }),
    ).then((a) => ({
      downloadUrl: a.download_url,
      filename: a.filename,
      contentType: a.content_type,
      uploadedAt: a.uploaded_at,
    })),

  /** Full replacement; returns the saved list. */
  setHandoffDestinations: (destinations: string[]): Promise<string[]> =>
    requestKeeping422(() =>
      apiClient.PUT("/phone-setup/handoff-destinations", {
        body: { destinations },
      }),
    ).then((saved) => saved.destinations),

  /** Applies only supplied fields. Disabling blocks new admissions only. */
  setOutboundConfig: (input: {
    enabled?: boolean;
    concurrencyLimit?: number;
    disclosureSpoken?: boolean;
    inboundRing?: InboundRingMode;
    outboundComputerDefault?: boolean;
    holdCheckInSeconds?: number;
    holdMaxSeconds?: number;
  }): Promise<{ enabled: boolean; concurrencyLimit: number }> =>
    requestKeeping422(() =>
      apiClient.PUT("/phone-setup/outbound", {
        body: {
          outbound_enabled: input.enabled,
          outbound_concurrency_limit: input.concurrencyLimit,
          outbound_disclosure_spoken: input.disclosureSpoken,
          inbound_ring: input.inboundRing,
          outbound_computer_default: input.outboundComputerDefault,
          hold_check_in_seconds: input.holdCheckInSeconds,
          hold_max_seconds: input.holdMaxSeconds,
        },
      }),
    ).then((saved) => ({
      enabled: saved.outbound_enabled,
      concurrencyLimit: saved.outbound_concurrency_limit,
    })),

  /** Full replacement; returns the saved list. */
  setOutboundLines: (lines: OutboundLineInput[]): Promise<OutboundLine[]> =>
    requestKeeping422(() =>
      apiClient.PUT("/phone-setup/outbound-endpoints", {
        body: {
          endpoints: lines.map((line) => ({
            label: line.label,
            address: line.address,
            enabled: line.enabled,
            ring_timeout_seconds: line.ringTimeoutSeconds,
          })),
        },
      }),
    ).then((saved) => saved.endpoints.map(toOutboundLine)),

  /** The provider calls the number to press 1; poll `getCallerId`. 503 without a provider, 502 when it refused. */
  startCallerIdVerification: (numberE164: string): Promise<CallerId> =>
    requestKeeping422(() =>
      apiClient.POST("/phone-setup/outbound/caller-id/verification", {
        body: { number_e164: numberE164 },
      }),
    ).then(toCallerId),

  /** Re-checked with the provider while pending. */
  getCallerId: (): Promise<CallerId> =>
    request(() =>
      apiClient.GET("/phone-setup/outbound/caller-id/verification", {}),
    ).then(toCallerId),

  clearCallerId: (): Promise<CallerId> =>
    request(() => apiClient.DELETE("/phone-setup/outbound/caller-id", {})).then(
      toCallerId,
    ),
};

// Validation below mirrors brian's rules so most 422s never leave the browser.

export const MAX_HANDOFF_DESTINATIONS = 5;
export const MESSAGE_TEXT_MAX_CHARS = 500;

// Mirrors brian's `ClinicPhoneConfig` hold bounds and defaults.

export const MAX_HOLD_MESSAGES = HOLD_MESSAGE_PURPOSES.length;
export const HOLD_CHECK_IN_MIN_SECONDS = 20;
export const HOLD_CHECK_IN_MAX_SECONDS = 120;
export const HOLD_CHECK_IN_DEFAULT_SECONDS = 45;
export const HOLD_MAX_DEFAULT_SECONDS = 600;
/** Must include `HOLD_MAX_DEFAULT_SECONDS`. */
export const HOLD_MAX_SECONDS_OPTIONS: readonly number[] = [
  120, 300, 600, 900, 1200, 1800,
];

export const isValidHoldCheckIn = (seconds: number): boolean =>
  Number.isInteger(seconds) &&
  seconds >= HOLD_CHECK_IN_MIN_SECONDS &&
  seconds <= HOLD_CHECK_IN_MAX_SECONDS;

export const AUDIO_MAX_BYTES = 5 * 1024 * 1024;

/** Safari labels wav audio/x-wav, Chrome audio/wav; Android labels mp3 audio/mp3. */
export const ALLOWED_AUDIO_TYPES = [
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/mpeg",
  "audio/mp3",
];

/** Extensions too, for pickers that match on those. */
export const AUDIO_ACCEPT = [...ALLOWED_AUDIO_TYPES, ".wav", ".mp3"].join(",");

// Mirrors brian's `_E164` (modules/phone/destinations.py): 8-15 digits, not E.164's 2-15.
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

const isE164 = (value: string): boolean => E164_PATTERN.test(value);

interface DestinationsValidation {
  rows: (string | undefined)[];
  list: string | null;
  valid: boolean;
}

export const validateDestinations = (
  numbers: string[],
  brianNumberE164: string | null,
): DestinationsValidation => {
  const seen = new Set<string>();
  const rows = numbers.map((number): string | undefined => {
    let error: string | undefined;
    if (number === "") error = "Enter a phone number.";
    else if (!isE164(number))
      error = "Use the full number with country code, e.g. +15551234567.";
    else if (brianNumberE164 !== null && number === brianNumberE164)
      error =
        "That's your Brian number — handing off to it would loop the call back here.";
    else if (seen.has(number)) error = "Already in the list.";
    seen.add(number);
    return error;
  });

  const list =
    numbers.length > MAX_HANDOFF_DESTINATIONS
      ? `At most ${MAX_HANDOFF_DESTINATIONS} destinations.`
      : null;

  return {
    rows,
    list,
    valid: list === null && rows.every((error) => error === undefined),
  };
};

/** Non-NANP numbers stay E.164 rather than guessing a foreign grouping. */
export const formatPhoneNumber = (e164: string): string => {
  const nanp = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return nanp ? `(${nanp[1]}) ${nanp[2]}-${nanp[3]}` : e164;
};

// Mirrors brian's `plan_outbound_endpoints` (modules/phone/outbound_endpoints.py).

export const MAX_OUTBOUND_LINES = 10;
export const RING_TIMEOUT_MIN_SECONDS = 5;
export const RING_TIMEOUT_MAX_SECONDS = 60;
export const RING_TIMEOUT_DEFAULT_SECONDS = 18;

export interface OutboundLineErrors {
  label?: string;
  address?: string;
  ringTimeoutSeconds?: string;
}

interface OutboundLinesValidation {
  rows: OutboundLineErrors[];
  list: string | null;
  valid: boolean;
}

/** Addresses compare after normalization: that is the identity brian's line claim locks on. */
export const validateOutboundLines = (
  lines: OutboundLineInput[],
  brianNumberE164: string | null,
  normalize: (typed: string) => string | null,
): OutboundLinesValidation => {
  const seenLabels = new Set<string>();
  const seenAddresses = new Set<string>();
  const rows = lines.map((line): OutboundLineErrors => {
    const errors: OutboundLineErrors = {};
    const label = line.label.trim();
    if (label === "") errors.label = "Give this line a name.";
    else if (seenLabels.has(label.toLowerCase()))
      errors.label = "Another line already has this name.";
    seenLabels.add(label.toLowerCase());

    if (
      !Number.isInteger(line.ringTimeoutSeconds) ||
      line.ringTimeoutSeconds < RING_TIMEOUT_MIN_SECONDS ||
      line.ringTimeoutSeconds > RING_TIMEOUT_MAX_SECONDS
    )
      errors.ringTimeoutSeconds = `Between ${RING_TIMEOUT_MIN_SECONDS} and ${RING_TIMEOUT_MAX_SECONDS} seconds.`;

    const address = normalize(line.address);
    if (line.address.trim() === "") errors.address = "Enter a phone number.";
    else if (address === null)
      errors.address = "That isn't a number Brian can dial.";
    else if (brianNumberE164 !== null && address === brianNumberE164)
      errors.address =
        "That's your Brian number — ringing it would have Brian answer itself.";
    else if (seenAddresses.has(address))
      errors.address = "Already in the list.";
    if (address) seenAddresses.add(address);
    return errors;
  });

  const list =
    lines.length > MAX_OUTBOUND_LINES
      ? `At most ${MAX_OUTBOUND_LINES} lines.`
      : null;

  return {
    rows,
    list,
    valid:
      list === null && rows.every((errors) => Object.keys(errors).length === 0),
  };
};
